import { useTranslation } from 'react-i18next';
import { lookupLabel } from '@/lib/lookups';
import type { LookupItem } from '@/types/shared';

interface LookupSelectProps {
  id: string;
  value: number | '';
  onChange: (value: number | '') => void;
  /** Full list (including inactive) so a previously-saved-but-now-inactive value can still be
   * rendered, greyed out — 10-design-system.md §4.3. */
  items: LookupItem[];
  placeholder: string;
  disabled?: boolean;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  /** For OPTIONAL, clearable fields (e.g. the referral-partner picker, T152): keeps the
   * placeholder/`""` option selectable even after a value has been chosen, so the user can
   * explicitly return to "no selection" from the control itself. Required-field callers
   * (grade/school/educational administration) omit this — the placeholder disables itself once a
   * value is picked, since those fields cannot be cleared back to empty. */
  allowClear?: boolean;
}

/** `LookupSelect` — async-loaded reference-table select (grades / agreedSchools / educational
 * administrations / referral partners). If the currently selected id is no longer active, it's
 * still rendered (so the user can see what was there) but disabled with a "(not available)"
 * suffix, forcing re-selection before save — 10-design-system.md §4.3 / 08-staff-ux.md §5.2. */
export function LookupSelect({
  id,
  value,
  onChange,
  items,
  placeholder,
  disabled,
  ariaInvalid,
  ariaDescribedBy,
  allowClear,
}: LookupSelectProps) {
  const { t, i18n } = useTranslation();
  const activeItems = items.filter((item) => item.isActive);
  const selectedItem = value === '' ? null : (items.find((item) => item.id === value) ?? null);
  const selectedIsInactive = Boolean(selectedItem && !selectedItem.isActive);

  return (
    <select
      id={id}
      value={value === '' ? '' : String(value)}
      disabled={disabled}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
    >
      <option value="" disabled={!allowClear && value !== ''}>
        {placeholder}
      </option>
      {activeItems.map((item) => (
        <option key={item.id} value={item.id}>
          {lookupLabel(item, i18n.language)}
        </option>
      ))}
      {selectedIsInactive && selectedItem && (
        <option value={selectedItem.id} disabled>
          {`${lookupLabel(selectedItem, i18n.language)} (${t('students.form.lookupUnavailable')})`}
        </option>
      )}
    </select>
  );
}
